import { useCallback, useEffect } from 'react';
import { getTasks } from '@connect-x-app/hybrid-data-access';
import {
  ApiError,
  FlightRoomSummary,
  FlightStatus,
  HttpStatus,
  Pagination,
  QueryKeys,
  WorkstationState,
} from '@connect-x-app/shared-domain-models';
import { DEFAULT_PAGE_SIZE, SortAscending } from '@connect-x-app/shared-utils';
import { InfiniteData, QueryKey, useInfiniteQuery } from '@tanstack/react-query';

import { useFlightRooms } from '../feature';

export const useGetPaginatedTasks = (enabled: boolean, workstation: WorkstationState | null = null) => {
  const { resetFlightRooms, setFlightRooms } = useFlightRooms();

  const updateTasks = useCallback(
    (pages: Pagination<FlightRoomSummary>[]) => {
      const tasks = pages.reduce((accu: FlightRoomSummary[], page) => [...accu, ...(page.items ?? [])], []);
      setFlightRooms(tasks, FlightStatus.TASKS);
    },
    [setFlightRooms],
  );

  const queryResult = useInfiniteQuery<
    Pagination<FlightRoomSummary>,
    ApiError,
    InfiniteData<Pagination<FlightRoomSummary>>,
    QueryKey,
    number
  >({
    enabled,
    gcTime: 0,
    getNextPageParam: ({ items, limit, offset }) => {
      if (items?.length !== limit) {
        return undefined;
      }

      return offset + limit;
    },
    initialPageParam: 0,
    queryFn: ({ pageParam }) => {
      const workstationId = workstation?.workstationId === '' ? undefined : workstation?.workstationId;
      return getTasks({
        limit: DEFAULT_PAGE_SIZE,
        offset: pageParam,
        sort: [new SortAscending<FlightRoomSummary>('schedule.std')],
        workstationId,
      });
    },
    queryKey: [QueryKeys.FLIGHTROOMS, FlightStatus.TASKS, workstation],
    retry: 0,
    staleTime: 0,
  });

  useEffect(() => {
    if (queryResult.data && enabled) {
      const pages = queryResult.data.pages;
      updateTasks(pages);
    }
  }, [enabled, queryResult.data, updateTasks]);

  useEffect(() => {
    if (queryResult.error?.statusCode === HttpStatus.FORBIDDEN) {
      resetFlightRooms();
    }
  }, [queryResult.error?.statusCode, resetFlightRooms]);

  return queryResult;
};
